import { COOKIE_CATEGORIES } from "@iec-id/shared/constants";
import {
	CookieBanner,
	type CookieBannerLabels,
	type CookieCategoryLabels,
	decideCookieSync,
	markCookieConsentSynced,
	syncCookieConsentFromServer,
	useCookieConsent,
} from "@iec-id/ui";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAnalyticsConsent } from "@/lib/analytics";
import { api } from "@/lib/trpc";
import { useLegalT } from "./i18n";

/**
 * Баннер cookie на экранах входа.
 *
 * Здесь человек чаще всего гость, поэтому выбор сохраняется в браузере —
 * записывать его больше некуда. Но вход с sso.iec.exchange нередко ведёт не в
 * витрину, а сразу в сервис (StudyHub, Hero), и человек может не открыть
 * `id.iec.exchange` никогда. Поэтому перенос выбора в реестр начинается уже
 * здесь: если сессия есть, а гостевое решение ещё не перенесено — переносим.
 *
 * Запрос сессии делается ТОЛЬКО когда есть что переносить (`enabled`): гость
 * без решения не должен получать лишний запрос на каждом открытии формы входа.
 */
export function CookieConsent() {
	const t = useLegalT();
	const { i18n } = useTranslation();
	const local = useCookieConsent();

	useAnalyticsConsent();

	const needsImport = local !== null && !local.synced;
	const session = api.auth.sessionHint.useQuery(
		{},
		{ enabled: needsImport, retry: false, refetchOnWindowFocus: false },
	);

	const save = api.consents.setCookies.useMutation({
		onSuccess: (data) => {
			syncCookieConsentFromServer({
				choices: data.choices,
				decidedAt: data.decidedAt ?? new Date().toISOString(),
				version: data.version,
				sha256: data.sha256,
			});
		},
	});

	// Перенос — один раз на монтирование: реестр append-only, и повторная
	// отправка того же решения дала бы вторую запись без нового решения.
	const imported = useRef(false);
	const saveMutate = save.mutate;

	useEffect(() => {
		if (imported.current) return;
		// Реестр на экранах входа не читается: тут решается ровно один вопрос —
		// «есть ли непёренесённый гостевой выбор при живой сессии». Состояние
		// самого реестра показывает витрина.
		const decision = decideCookieSync({
			local,
			server: {
				decided: false,
				choices: { preferences: false, analytics: false },
				decidedAt: null,
				version: null,
				sha256: null,
			},
			authenticated: session.data?.state === "session",
		});
		if (decision.action !== "import") return;
		imported.current = true;
		// Редакция не передаётся намеренно — см. `id-web/.../use-cookie-registry.ts`:
		// гость решал по тексту неизвестной давности, сверка хеша сорвала бы перенос.
		saveMutate({ choices: decision.choices, source: "guest_import" });
		markCookieConsentSynced();
	}, [session.data, local, saveMutate]);

	return (
		<CookieBanner
			labels={cookieBannerLabels(t)}
			documentHref={`/legal/cookies?lang=${i18n.language}`}
			renderLink={({ children }) => (
				<Link
					to="/legal/$key"
					params={{ key: "cookies" }}
					search={{ lang: i18n.language }}
					className="underline"
				>
					{children}
				</Link>
			)}
		/>
	);
}

/** Тексты баннера. Кит переводов не знает — строки собираются здесь. */
function cookieBannerLabels(t: ReturnType<typeof useLegalT>): CookieBannerLabels {
	const categories = Object.fromEntries(
		COOKIE_CATEGORIES.map((category) => [
			category.key,
			{
				label: t(`categories.${category.key}.label` as const),
				hint: t(`categories.${category.key}.hint` as const),
			} satisfies CookieCategoryLabels,
		]),
	) as CookieBannerLabels["categories"];

	return {
		title: t("banner.title"),
		description: t("banner.description"),
		learnMore: t("banner.learnMore"),
		acceptAll: t("banner.acceptAll"),
		rejectAll: t("banner.rejectAll"),
		configure: t("banner.configure"),
		back: t("banner.back"),
		save: t("banner.save"),
		alwaysOn: t("banner.alwaysOn"),
		close: t("banner.close"),
		categories,
	};
}
